// Path: ./src/app/pages/CreateFlyerItemPage/useFlyerItemTableColumns.tsx
// setTableColumn.tsx

import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import { GfFlyerItemDto, SettingKey } from "@swagger/typescript-fetch-goflyer";
import { RenderMultiImageTable } from "../../components/RenderImageTable";
import { EditableCell } from "../../components/EditableCell";
import { useHandleSaveFlyerItem } from "./CreateFlyerModal/handleSaveFlyerItem";
import { setWebsite } from "../../../utils/getShowAt";
import { useMemo, useCallback } from "react";
import { Chip } from "@mui/material";
import {
  useTableSettingsConfig,
  applyColumnsConfig,
} from "../../../hooks/useTableSettingsConfig";

export const useFlyerItemTableColumns = (
  handleOnClickEdit: (value: GfFlyerItemDto) => Promise<void>,
  onRefreshData: (itemId?: string) => Promise<void>,
) => {
  const { t } = useTranslation();
  const handleSaveFlyerItem = useHandleSaveFlyerItem();
  const { columnsConfig } = useTableSettingsConfig(SettingKey.TableFlyerItems);

  const editableCellRenderer = useCallback(
    (fieldName: keyof GfFlyerItemDto, isNumber = false) => {
      return (params: GridRenderCellParams<GfFlyerItemDto>) => (
        <EditableCell
          value={
            typeof params.row[fieldName] === "string" ||
            typeof params.row[fieldName] === "number"
              ? (params.row[fieldName] as string | number)
              : ""
          }
          type={isNumber ? "number" : "text"}
          onSave={async (newValue) => {
            await handleSaveFlyerItem(
              setWebsite(params.row?.showAt).storeWebsite,
              setWebsite(params.row?.showAt).goflyerWebsite,
              {
                ...params.row,
                [fieldName]: newValue,
              },
            );
            onRefreshData(params.row.id);
          }}
        />
      );
    },
    [handleSaveFlyerItem, onRefreshData],
  );

  const values: AllowedTableColumn[] = ["createdAt", "updatedAt"];

  const baseColumns = useMemo(
    () => [
      {
        field: "edit",
        filterable: false,
        width: 90,
        headerName: `${t(messages.Edit())}`,
        renderCell: (props: GridRenderCellParams) => {
          return (
            <RenderEditButton
              onClick={handleOnClickEdit}
              {...props}
            ></RenderEditButton>
          );
        },
        sortable: false,
      },
      {
        filterable: false,
        field: "gfImages",
        headerName: `${t(messages["gfImages"]())}`,
        renderCell: (props: GridRenderCellParams<GfFlyerItemDto>) => {
          return (
            <RenderMultiImageTable
              images={props.row.gfMultiImages?.map((val) => val.gfImage)}
            ></RenderMultiImageTable>
          );
        },
        sortable: false,
      },
      {
        field: "name",
        headerName: `${t(messages["name"]())}`,
        width: 200,
        renderCell: editableCellRenderer("name"),
      },
      {
        field: "nameChinese",
        headerName: `${t(messages["nameChinese"]())}`,
        width: 200,
        renderCell: editableCellRenderer("nameChinese"),
      },
      {
        field: "rank",
        type: "number",
        headerName: `${t(messages["Rank"]())}`,
        width: 80,
        renderCell: editableCellRenderer("rank", true),
      },
      {
        field: "quantity",
        type: "number",
        headerName: `${t(messages["Quantity"]())}`,
        sortable: false,
        width: 65,
      },

      {
        field: "salePrice",
        type: "number",
        headerName: `${t(messages["salePrice"]())}`,
        width: 100,
        renderCell: editableCellRenderer("salePrice", true),
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) =>
          `$${params.row.salePrice}`,
      },
      {
        field: "regularPriceString",
        headerName: `${t(messages["regularPrice"]())}`,
        width: 100,
        renderCell: editableCellRenderer("regularPriceString"),
      },
      {
        field: "topSale",
        type: "boolean",
        headerName: `${t(messages["topSale"]())}`,
        width: 90,
      },
      {
        field: "showAt",
        type: "singleSelect",
        valueOptions: [
          { value: GfFlyerItemDto.ShowAtEnum.None, label: "None" },
          { value: GfFlyerItemDto.ShowAtEnum.Both, label: "Both" },
          { value: GfFlyerItemDto.ShowAtEnum.Goflyer, label: "Goflyer" },
          {
            value: GfFlyerItemDto.ShowAtEnum.Supermarket,
            label: "Supermarket",
          },
        ],
        headerName: `${t(messages["ShowAt"]())}`,
        width: 80,
      },
      {
        field: "gfFlyerItemCategory.name",
        width: 155,
        sortable: false,
        headerName: `${t(messages["gfFlyerItemCategory"]())}`,
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const flyerTag = params.row.gfFlyerItemCategory;
          return flyerTag?.map((tag) => tag.name).join(", ");
        },
      },
      {
        field: "unit",
        headerName: `${t(messages["Unit"]())}`,
        width: 100,
      },
      {
        field: "priceRemarks",
        headerName: `${t(messages["Price_Remarks"]())}`,
        width: 150,
      },
      {
        field: "limitString",
        headerName: `${t(messages["Limit"]())}`,
        width: 100,
      },
      {
        field: "promotionTag",
        headerName: `${t(messages["Promotion_Tag"]())}`,
        width: 100,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const promotionTag = params.row.promotionTag;
          return promotionTag?.map((tag) => tag.name).join(", ");
        },
      },
      {
        field: "flyerItemMetaData",
        filterable: false,
        sortable: false,
        headerName: `${t(messages["MetaData"]())}`,
        width: 250,
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const metaData = params.row.flyerItemMetaData;
          return metaData
            ?.map(
              (data) =>
                `${data.key}: ${data.value}${
                  data.type ? ` (${data.type})` : ""
                }`,
            )
            .join(", ");
        },
      },
      {
        field: "itemSource",
        width: 200,
        headerName: `Item Source`,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const smartFlyer = params?.row?.itemSource;

          return smartFlyer ? (
            <Chip
              label={params?.row?.itemSource}
              size="small"
              color="info"
              sx={{
                fontSize: "12px",
                height: "22px",
                backgroundColor: "#f58220",
                color: "#fff",
                fontWeight: "500",
              }}
            />
          ) : (
            <></>
          );
        },
      },
      ...values.map((value) => {
        return {
          width: 200,
          field: value,
          headerName: `${t(messages[value]())}`,
          type: "dateTime",
          valueGetter: (val: GridRenderCellParams) => {
            return (val && value === "validEndDate") ||
              value === "validStartDate"
              ? new Date(val?.row.flyerItemList[value])
              : new Date(val?.row[value]);
          },
        };
      }),
      {
        field: "CreatedBy",
        filterable: false,
        width: 200,
        headerName: `${t(messages["CreatedBy"]())}`,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const { firstName, lastName, userName, email } =
            params?.row?.adminUser || {};
          const createdBy = firstName
            ? `${firstName} ${lastName || ""}`
            : userName || email || "";

          return createdBy;
        },
      },
      {
        field: "goflyer_website",
        filterable: false,
        width: 100,
        headerName: `${t(messages.Goflyer_website_APP())}`,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const showAt =
            params?.row?.showAt === GfFlyerItemDto.ShowAtEnum.Goflyer ||
            params?.row?.showAt === GfFlyerItemDto.ShowAtEnum.Both;

          return showAt;
        },
      },

      {
        field: "store_website",
        filterable: false,
        width: 100,
        headerName: `${t(messages.Store_website_APP())}`,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<GfFlyerItemDto>) => {
          const showAt =
            params?.row?.showAt === GfFlyerItemDto.ShowAtEnum.Supermarket ||
            params?.row?.showAt === GfFlyerItemDto.ShowAtEnum.Both;

          return showAt;
        },
      },
      {
        field: "id",
        filterable: false,
        width: 300,
        headerName: `${t(messages["id"]())}`,
      },
    ],
    [handleOnClickEdit, handleSaveFlyerItem, onRefreshData, t],
  );

  const columns = useMemo(
    () => applyColumnsConfig(baseColumns, columnsConfig),
    [baseColumns, columnsConfig],
  );

  return columns;
};
